import { Middleware, Reducer, SerializedError, UnknownAction, isRejected } from "@reduxjs/toolkit";

import { ERROR_IMPACT, trackFailure } from "@wevideo/platform/errorTracking";
import { isBrowserOnline } from "@wevideo/platform/runtime/Environment";

type StoreContext = { runtimeContext?: string };
type StoreFailureOptions = StoreContext & { apiReducerPath?: string };

const isAnyRejected = isRejected();
type RejectedAction = typeof isAnyRejected extends (action: unknown) => action is infer A
	? A
	: never;

const REMEMBERED_STACKS = 32;
const SESSION_EXPIRED_MESSAGE = /status code 401$/;

const toError = (thrown: unknown) => (thrown instanceof Error ? thrown : new Error(String(thrown)));

const actionType = (action: unknown) => {
	const type = (action as { type?: unknown } | null)?.type;
	return typeof type === "string" ? type : undefined;
};

const fromSerializedError = (serialized: SerializedError) => {
	const error = new Error(serialized.message ?? "Rejected");
	if (serialized.name) error.name = serialized.name;
	if (serialized.stack) error.stack = serialized.stack;
	return error;
};

const isUnhandledRejection = (action: unknown): action is RejectedAction =>
	isAnyRejected(action) &&
	!action.meta.rejectedWithValue &&
	!action.meta.aborted &&
	!action.meta.condition;

const mutationArg = (action: RejectedAction) => {
	const arg = action.meta.arg as { type?: unknown; endpointName?: unknown } | null | undefined;
	return arg && arg.type === "mutation" && typeof arg.endpointName === "string"
		? arg.endpointName
		: undefined;
};

const rejectionStatus = (action: RejectedAction) => {
	const payload = action.payload as { status?: unknown } | null | undefined;
	return typeof payload?.status === "number" ? payload.status : undefined;
};

const isServerFailure = (status: number | undefined) => status === undefined || status >= 500;

export const reportSagaFailure =
	(context: StoreContext = {}) =>
	(thrown: unknown, info: { sagaStack: string }) => {
		trackFailure(toError(thrown), {
			impact: ERROR_IMPACT.FLOW_FAILURE,
			source: "saga",
			detail: info.sagaStack,
			...context,
		});
	};

export const createStoreFailureReporting = ({
	apiReducerPath,
	...context
}: StoreFailureOptions = {}) => {
	const reportedStacks = new Set<string>();

	const remember = (stack: string | undefined) => {
		if (!stack) return;
		reportedStacks.add(stack);
		if (reportedStacks.size > REMEMBERED_STACKS) {
			const oldest = reportedStacks.values().next();
			if (!oldest.done) reportedStacks.delete(oldest.value);
		}
	};

	const wrapReducer = <R extends (state: never, action: never) => unknown>(reducer: R): R =>
		((state: unknown, action: UnknownAction) => {
			try {
				return (reducer as unknown as Reducer<unknown, UnknownAction>)(state, action);
			} catch (thrown) {
				try {
					const error = toError(thrown);
					remember(error.stack);
					trackFailure(error, {
						impact: ERROR_IMPACT.FLOW_FAILURE,
						source: "reducer",
						operation: actionType(action),
						...context,
					});
				} catch {
					// The caller gets the original error.
				}
				throw thrown;
			}
		}) as unknown as R;

	const isApiAction = (action: RejectedAction) =>
		apiReducerPath !== undefined && action.type.startsWith(`${apiReducerPath}/`);

	const reportThunkFailure = (action: RejectedAction) => {
		if (isApiAction(action)) return;
		if (SESSION_EXPIRED_MESSAGE.test(action.error.message ?? "")) return;
		if (action.error.stack && reportedStacks.has(action.error.stack)) return;
		const operation = action.type.replace(/\/rejected$/, "");
		trackFailure(fromSerializedError(action.error), {
			impact: ERROR_IMPACT.FLOW_FAILURE,
			source: "thunk",
			operation,
			...context,
			...(action.error.stack ? {} : { fingerprint: ["flow_failure", "thunk", operation] }),
		});
	};

	const reportMutationFailure = (action: RejectedAction) => {
		const endpointName = mutationArg(action);
		if (!endpointName || !isApiAction(action) || action.meta.aborted) return;
		// A thrown endpoint is already captured through RTK Query's own console.error.
		if (!action.meta.rejectedWithValue) return;
		const status = rejectionStatus(action);
		if (!isServerFailure(status)) return;
		const statusLabel = status === undefined ? "no_response" : String(status);
		trackFailure(
			new Error(
				status === undefined
					? `Mutation ${endpointName} rejected without a response`
					: `Mutation ${endpointName} rejected with status ${status}`
			),
			{
				impact: ERROR_IMPACT.FLOW_FAILURE,
				source: "api_mutation",
				operation: endpointName,
				...context,
				fingerprint: ["flow_failure", "api_mutation", endpointName, statusLabel],
			}
		);
	};

	const middleware: Middleware = () => next => action => {
		const result = next(action);
		try {
			if (isBrowserOnline() && isAnyRejected(action)) {
				if (isUnhandledRejection(action)) reportThunkFailure(action);
				else reportMutationFailure(action);
			}
		} catch {
			// Reporting never affects the dispatch result.
		}
		return result;
	};

	return { wrapReducer, middleware };
};
